import { useState } from 'react'

import { HttpStatus } from 'enums'
import { PortfolioViewState } from 'enums/PortfolioViewState'
import { Portfolio } from 'types'

import { paveApi } from 'api'
import ModalLoader from 'components/common/Loader/ModalLoader'
import InitiateWithdrawalModal from 'components/partials/Portfolio/V2/Modals/InitiateWithdrawalModal'
import CashCardTooltipContent from 'components/partials/Portfolio/V2/TooltipContent/CashCardTooltipContent'
import { DEFAULT_TARGET_CASH_WEIGHT } from 'constants/portfolio.constants'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSCard from 'designSystem/components/PDSCard/PDSCard'
import PDSCardHeader from 'designSystem/components/PDSCardHeader/PDSCardHeader'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSIcon from 'designSystem/components/PDSIcon/PDSIcon'
import PDSTag from 'designSystem/components/PDSTag/PDSTag'
import PDSText from 'designSystem/components/PDSText/PDSText'
import PDSTooltip from 'designSystem/components/PDSTooltip/PDSTooltip'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'
import { useCustomToast } from 'hooks/useCustomToast'
import { formatAsCurrency } from 'utils'
import { getPortfolioViewState, shouldLockPortfolio } from 'utils/portfolioUtil'

import PortfolioConfirmRebalanceCashModal from './Modals/PortfolioConfirmRebalanceCashModal'

type Props = {
  portfolio: Portfolio
}

export default function PortfolioCashCard({ portfolio }: Props) {
  const [showRebalanceModal, setShowRebalanceModal] = useState(false)
  const [showInitiateWithdrawalModal, setShowInitiateWithdrawalModal] =
    useState(false)
  const { showErrorToast, showSuccessToast, showViewOnlyDefaultToast } =
    useCustomToast()
  const [rebalancePortfolio, { isLoading: isRebalanceLoading }] =
    paveApi.useRebalancePortfolioMutation()

  const [
    markCashWithdrawalCompleteAsComplete,
    { isLoading: isMarkWithdrawalLoading },
  ] = paveApi.useMarkCashWithdrawalCompleteAsCompleteMutation()

  const {
    investmentPreference,
    isInvestmentPreferenceCompleted,
    recommendedAssets,
  } = portfolio

  const isLocked = shouldLockPortfolio(portfolio)
  const portfolioViewState = getPortfolioViewState(portfolio)
  const showCardContent = portfolioViewState === PortfolioViewState.NORMAL

  const availableCash = portfolio?.availableCash ?? 0
  const portfolioValue = portfolio?.value ?? 0

  const withdrawalCashRequest = investmentPreference?.withdrawalCashRequest
  const withdrawalCashAmount = withdrawalCashRequest?.cashAmount ?? 0.0
  const targetCashWeight =
    investmentPreference?.targetCashWeight ?? DEFAULT_TARGET_CASH_WEIGHT

  const currentCashAllocation =
    portfolioValue === 0 ? 0 : availableCash / portfolioValue
  const cashAllocationDiff = currentCashAllocation - targetCashWeight

  const hasActiveWithdrawal = !!withdrawalCashAmount
  const isCurrentCashAboveTarget = cashAllocationDiff > 0.0
  const showWithdrawalButton =
    portfolioValue > 0 && Boolean(portfolio?.isInvestmentPreferenceCompleted)

  const isRebalanceDisabled =
    hasActiveWithdrawal ||
    recommendedAssets?.length === 0 ||
    isLocked ||
    !isCurrentCashAboveTarget ||
    isRebalanceLoading ||
    !isInvestmentPreferenceCompleted

  const showLoader = isRebalanceLoading || isMarkWithdrawalLoading

  const handleRebalancePortfolio = () => {
    setShowRebalanceModal(true)
  }

  const confirmRebalancePortfolio = function () {
    const errorMessage = 'An error occurred investing your excess cash'
    if (!portfolio) {
      showErrorToast(errorMessage)
      return
    }

    rebalancePortfolio(portfolio.id)
      .unwrap()
      .then(() => {
        showSuccessToast(
          'Suggested trades generated',
          'A new set of suggested trades was successfully generated for your portfolio'
        )
      })
      .catch((e) => {
        if (e?.status === HttpStatus.FORBIDDEN) {
          showViewOnlyDefaultToast()
        } else {
          showErrorToast(errorMessage)
        }
      })
      .finally(() => {
        setShowRebalanceModal(false)
      })
  }

  const handleInitiateWithdrawal = () => {
    setShowInitiateWithdrawalModal(true)
  }

  const handleMarkWithdrawalComplete = () => {
    if (portfolio?.id) {
      markCashWithdrawalCompleteAsComplete({ id: portfolio.id })
        .unwrap()
        .then(() => {
          showSuccessToast('Successfully marked withdrawal as completed!')
        })
        .catch((e) => {
          if (e?.status === HttpStatus.FORBIDDEN) {
            showViewOnlyDefaultToast()
          } else {
            showErrorToast(
              'An error occurred when marking the withdrawal as complete.'
            )
          }
        })
    }
  }

  return (
    <>
      <PDSCard>
        <PDSCardHeader>
          <PDSFlexBox alignItems="center" gap={SIZE.s8}>
            <PDSHeader variant="h4">Cash</PDSHeader>
            <PDSTooltip content={<CashCardTooltipContent />} />
          </PDSFlexBox>
        </PDSCardHeader>
        {showCardContent && (
          <PDSBox>
            <PDSBox flexDirection="column" gap={SIZE.s12}>
              <PDSTag size="sm" variant="neutral">
                USD
              </PDSTag>

              <PDSBox marginTop={SIZE.s16}>
                <PDSText fontSize={FONT_SIZE.fs26} weight="semibold">
                  {formatAsCurrency(availableCash ?? 0)}
                </PDSText>
              </PDSBox>
              <PDSFlexBox
                alignItems="center"
                gap={SIZE.s4}
                marginTop={SIZE.s8}
                rowGap={SIZE.s8}
              >
                <PDSText color={COLOR.textSecondary}>
                  {(currentCashAllocation * 100)?.toFixed(2) || '0.00'}%
                </PDSText>
                {isCurrentCashAboveTarget && (
                  <>
                    <PDSText>•</PDSText>
                    <PDSText color={COLOR.textSecondary} weight="semibold">
                      {(cashAllocationDiff * 100)?.toFixed(2)}% over target
                    </PDSText>
                  </>
                )}
              </PDSFlexBox>
            </PDSBox>
            {hasActiveWithdrawal && (
              <PDSBox marginTop={SIZE.s20}>
                <PDSFlexBox alignItems="center">
                  <PDSIcon
                    color={COLOR.textSecondary}
                    marginRight={SIZE.s8}
                    name="cash"
                    size="md"
                  />
                  <PDSText color={COLOR.textSecondary} weight="semibold">
                    To be withdrawn:
                  </PDSText>
                </PDSFlexBox>
                <PDSFlexBox
                  alignItems="center"
                  border={`1px solid ${COLOR.info400}`}
                  backgroundColor={COLOR.info200}
                  borderRadius={SIZE.s8}
                  marginTop={SIZE.s12}
                  padding={SIZE.s24}
                >
                  <PDSText fontSize={FONT_SIZE.fs26} weight="medium">
                    {withdrawalCashAmount
                      ? formatAsCurrency(withdrawalCashAmount)
                      : '$0.00'}
                  </PDSText>
                </PDSFlexBox>
              </PDSBox>
            )}

            <PDSFlexBox
              flexDirection={hasActiveWithdrawal ? 'column-reverse' : 'column'}
              gap={SIZE.s12}
              marginTop={SIZE.s16}
            >
              <PDSButton
                disabled={isRebalanceDisabled}
                onClick={handleRebalancePortfolio}
                size="md-f"
                testId="invest-excess-cash"
                text="Invest Excess Cash"
                variant="secondary"
              />
              {showWithdrawalButton && (
                <PDSButton
                  disabled={isLocked}
                  onClick={
                    hasActiveWithdrawal
                      ? handleMarkWithdrawalComplete
                      : handleInitiateWithdrawal
                  }
                  size="md-f"
                  testId={
                    hasActiveWithdrawal
                      ? 'mark-withdrawal-complete'
                      : 'initiate-withdrawal'
                  }
                  text={
                    hasActiveWithdrawal
                      ? 'Mark Withdrawal Complete'
                      : 'Initiate Withdrawal'
                  }
                  variant={hasActiveWithdrawal ? 'secondary' : 'tertiary'}
                />
              )}
            </PDSFlexBox>
          </PDSBox>
        )}
      </PDSCard>
      <PortfolioConfirmRebalanceCashModal
        isDisabled={isRebalanceLoading}
        isOpen={showRebalanceModal}
        onCancel={() => setShowRebalanceModal(false)}
        onConfirm={confirmRebalancePortfolio}
      />
      <InitiateWithdrawalModal
        isOpen={showInitiateWithdrawalModal}
        onClose={() => setShowInitiateWithdrawalModal(false)}
        portfolio={portfolio}
      />
      {showLoader && <ModalLoader />}
    </>
  )
}
